/**
 * The questionnaire as JSON, and back.
 *
 * The fields and the JSON box are two views of the same content, so this module is the single
 * place that converts between them — the same arrangement the opt-in texts use
 * (`OptInFeatures/Utils/textsJson.ts`), and the gain is larger here: eight questions with options
 * in three languages is far more typing than ten fields.
 *
 * **The JSON carries content only**: the title, the description and the questions, in the three
 * languages. The BU, the slug, the delivery window, the trigger, the layout and the whole
 * eligibility block are deliberately absent. They belong to identification and targeting, and a
 * slug or an opt-in condition arriving from a pasted document is the easiest way to aim a survey
 * at the wrong people.
 *
 * `key` and `value` **are** in the document, and they are one code for the three languages — the
 * document says so, because an AI asked to "translate everything" will happily translate an
 * identifier that is written into every answer row.
 *
 * Parsing is **lenient on purpose**. It runs on every keystroke, so it only rejects what cannot be
 * read at all (a syntax error, or a root that is not an object); anything missing becomes empty.
 * Strict validation would wipe the fields the moment someone typed an opening brace.
 *
 * @module Pages/BUs/Surveys
 */

import type { MatrixRowType, SurveyLanguage, SurveyQuestionType } from '../Types/Surveys.types';
import {
  MATRIX_ROW_TYPES,
  SURVEY_LANGUAGES,
  SURVEY_LIMITS,
  SURVEY_QUESTION_TYPES,
} from '../Types/Surveys.types';
import type {
  ByLanguage,
  QuestionConfigNumbers,
  QuestionConfigTexts,
  SurveyFormData,
  SurveyOptionDraft,
  SurveyQuestionDraft,
  SurveyRowDraft,
  SurveyTextsDraft,
} from './surveyForm';
import {
  BASE_LANGUAGE,
  CONFIG_TEXT_FIELDS,
  EMPTY_CONFIG_TEXTS,
  configFieldsFor,
  defaultNumbersFor,
  emptyByLanguage,
  nextUid,
} from './surveyForm';

/** The content half of the form — what the JSON box shows. */
export interface QuestionnaireContent {
  texts: Record<SurveyLanguage, SurveyTextsDraft>;
  questions: SurveyQuestionDraft[];
}

const LANGUAGE_NAME: Record<SurveyLanguage, string> = {
  en: 'Inglês',
  pt: 'Português',
  es: 'Espanhol',
};

const byLanguageEntry = (values: ByLanguage): Record<string, string> => ({
  en: values.en,
  pt: values.pt,
  es: values.es,
});

/** Only the config a type actually uses, so the document does not carry noise per question. */
const configDocument = (question: SurveyQuestionDraft): Record<string, unknown> | undefined => {
  const fields = configFieldsFor(question.type);
  const document: Record<string, unknown> = {};

  fields.numbers.forEach(field => {
    const value = question.numbers[field];
    if (value !== undefined) document[field] = value;
  });

  fields.texts.forEach(field => {
    document[field] = {
      en: question.configTexts.en[field],
      pt: question.configTexts.pt[field],
      es: question.configTexts.es[field],
    };
  });

  return Object.keys(document).length > 0 ? document : undefined;
};

/**
 * Content as the JSON the box shows.
 *
 * Key order is fixed and follows the order of the editor, so editing a field does not reshuffle
 * the document under the cursor.
 */
export const serializeQuestionnaire = (content: QuestionnaireContent): string => {
  const document = {
    title: {
      en: content.texts.en.title,
      pt: content.texts.pt.title,
      es: content.texts.es.title,
    },
    description: {
      en: content.texts.en.description,
      pt: content.texts.pt.description,
      es: content.texts.es.description,
    },
    questions: content.questions.map(question => {
      const config = configDocument(question);
      const carriesOptions =
        question.type === 'SINGLE_CHOICE' ||
        question.type === 'MULTI_CHOICE' ||
        question.type === 'MATRIX';

      return {
        key: question.key,
        type: question.type,
        step: question.step,
        isRequired: question.isRequired,
        label: byLanguageEntry(question.labels),
        helpText: byLanguageEntry(question.helpTexts),
        ...(config ? { config } : {}),
        ...(carriesOptions
          ? {
              options: question.options.map(option => ({
                value: option.value,
                label: byLanguageEntry(option.labels),
                ...(option.isExclusive ? { isExclusive: true } : {}),
                ...(option.allowsFreeText ? { allowsFreeText: true } : {}),
              })),
            }
          : {}),
        ...(question.type === 'MATRIX'
          ? {
              rows: question.rows.map(row => ({
                key: row.key,
                type: row.type,
                isRequired: row.isRequired,
                label: byLanguageEntry(row.labels),
              })),
            }
          : {}),
      };
    }),
  };

  return JSON.stringify(document, null, 2);
};

/** The document to start from: one question of each shape worth copying. */
export const createQuestionnaireTemplate = (): string =>
  JSON.stringify(
    {
      title: {
        en: 'How is your experience with the new menu?',
        pt: 'Como está sua experiência com o novo menu?',
        es: '¿Cómo es tu experiencia con el nuevo menú?',
      },
      description: { en: '', pt: '', es: '' },
      questions: [
        {
          key: 'nota_geral',
          type: 'RATING',
          step: 1,
          isRequired: true,
          label: {
            en: 'How would you rate the new menu?',
            pt: 'Que nota você dá para o novo menu?',
            es: '¿Qué nota le das al nuevo menú?',
          },
          helpText: { en: '', pt: '', es: '' },
          config: {
            min: 1,
            max: 5,
            minLabel: { en: 'Very bad', pt: 'Muito ruim', es: 'Muy malo' },
            maxLabel: { en: 'Very good', pt: 'Muito boa', es: 'Muy bueno' },
          },
        },
        {
          key: 'o_que_mais_usa',
          type: 'MULTI_CHOICE',
          step: 2,
          isRequired: true,
          label: {
            en: 'Which sections do you use the most?',
            pt: 'Quais seções você mais usa?',
            es: '¿Qué secciones usas más?',
          },
          helpText: { en: 'Pick up to 3.', pt: 'Escolha até 3.', es: 'Elige hasta 3.' },
          config: { minSelections: 1, maxSelections: 3 },
          options: [
            {
              value: 'monitoramento',
              label: {
                en: 'Monitoring & Quality',
                pt: 'Monitoramento & Qualidade',
                es: 'Monitoreo y Calidad',
              },
            },
            {
              value: 'outro',
              label: { en: 'Another one', pt: 'Outra', es: 'Otra' },
              allowsFreeText: true,
            },
            {
              value: 'nenhuma',
              label: { en: 'None of them', pt: 'Nenhuma delas', es: 'Ninguna de ellas' },
              isExclusive: true,
            },
          ],
        },
        {
          key: 'avaliacao_menu',
          type: 'MATRIX',
          step: 3,
          isRequired: true,
          label: {
            en: 'Rate each aspect of the new menu',
            pt: 'Avalie cada aspecto do novo menu',
            es: 'Evalúa cada aspecto del nuevo menú',
          },
          helpText: { en: '', pt: '', es: '' },
          options: [
            { value: 'ruim', label: { en: 'Bad', pt: 'Ruim', es: 'Malo' } },
            { value: 'regular', label: { en: 'Fair', pt: 'Regular', es: 'Regular' } },
            { value: 'bom', label: { en: 'Good', pt: 'Bom', es: 'Bueno' } },
            { value: 'otimo', label: { en: 'Great', pt: 'Ótimo', es: 'Excelente' } },
          ],
          rows: [
            {
              key: 'organizacao',
              type: 'SINGLE_CHOICE',
              isRequired: true,
              label: {
                en: 'How the sections are organised',
                pt: 'Organização das seções',
                es: 'Organización de las secciones',
              },
            },
          ],
        },
        {
          key: 'comentario',
          type: 'LONG_TEXT',
          step: 4,
          isRequired: false,
          label: {
            en: 'Anything else you would like to tell us?',
            pt: 'Quer comentar alguma coisa?',
            es: '¿Quieres comentar algo?',
          },
          helpText: { en: '', pt: '', es: '' },
          config: {
            maxLength: 1000,
            placeholder: { en: 'Write here...', pt: 'Escreva aqui...', es: 'Escribe aquí...' },
          },
        },
      ],
    },
    null,
    2,
  );

/**
 * A prompt to paste into an AI, which answers with the document filled in.
 *
 * The template is interpolated from {@link createQuestionnaireTemplate}, not written out again:
 * the instruction and the shape it asks for cannot fall out of step that way.
 *
 * It spends most of its words on the two things an AI gets wrong here: translating identifiers
 * that must not be translated, and writing questions whose answers cannot be counted.
 */
export const createQuestionnairePrompt =
  (): string => `Você vai preencher um JSON que define um questionário curto exibido dentro de um
dashboard, em modal. Quem responde é usuário de um produto, no meio do trabalho.

PESQUISA: <descreva aqui o que você quer descobrir, com quem, e o que vai fazer com a resposta>

Tipos de pergunta disponíveis (use o nome exato):

- \`SHORT_TEXT\` — uma linha. \`config.maxLength\` e \`config.placeholder\`
- \`LONG_TEXT\` — parágrafo. Idem
- \`SINGLE_CHOICE\` — uma opção entre várias
- \`MULTI_CHOICE\` — várias opções. \`config.minSelections\` e \`config.maxSelections\`
- \`RATING\` — escala numérica. \`config.min\`, \`config.max\`, \`config.minLabel\`, \`config.maxLabel\`
- \`NPS\` — 0 a 10 fixo. \`config.leftLabel\` e \`config.rightLabel\`
- \`BOOLEAN\` — sim/não. \`config.trueLabel\` e \`config.falseLabel\`
- \`MATRIX\` — grade: uma escala (em \`options\`) avaliada linha a linha (em \`rows\`).
  As linhas NÃO têm opções próprias, herdam a escala da pergunta mãe

Regras que não podem ser quebradas:

- \`key\` (da pergunta e da linha de grade) e \`value\` (da opção) são CÓDIGOS INTERNOS.
  Minúsculo, sem acento, palavras separadas por \`_\`. **Não traduza nenhum dos dois**:
  eles são gravados em cada resposta e viram o nome da coluna no CSV. Um só código
  serve os três idiomas
- Preencha os três idiomas de cada texto: \`en\` (inglês), \`pt\` (português do Brasil),
  \`es\` (espanhol). Traduza o sentido, não palavra a palavra
- \`step\` agrupa as perguntas por tela. Comece em 1 e não pule números. De 2 a 4
  perguntas por passo; no máximo ${SURVEY_LIMITS.steps} passos
- No máximo ${SURVEY_LIMITS.questions} perguntas, ${SURVEY_LIMITS.optionsPerQuestion} opções por pergunta e ${SURVEY_LIMITS.rowsPerMatrix} linhas por grade
- Uma pergunta por ideia. Pergunta dupla ("o menu é rápido e fácil?") produz uma
  resposta que não se sabe interpretar
- Em listas de opções, cubra as alternativas plausíveis e acrescente uma opção com
  \`allowsFreeText: true\` quando a lista puder estar incompleta. Uma opção "nenhuma
  delas" leva \`isExclusive: true\`
- Deixe as perguntas abertas por último e opcionais (\`isRequired: false\`): obrigar a
  escrever é o que mais faz abandonar o questionário
- Tom direto, segunda pessoa, sem jargão de produto e sem exclamação
- Responda APENAS com o JSON, sem comentário nem cerca de código

TEMPLATE (preencha exatamente esta estrutura):

${createQuestionnaireTemplate()}`;

export type ParseQuestionnaireResult =
  | { ok: true; content: QuestionnaireContent }
  | { ok: false; message: string };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Reads one text.
 *
 * Accepts two shapes, because two kinds of document get pasted here: the box's own
 * `{ en, pt, es }`, and a plain string — which is what copying a field out of the admin payload
 * gives, where the base column is already English.
 */
const readText = (source: unknown): ByLanguage => {
  if (typeof source === 'string') return { ...emptyByLanguage(), en: source };
  if (!isRecord(source)) return emptyByLanguage();

  return {
    en: typeof source['en'] === 'string' ? source['en'] : '',
    pt: typeof source['pt'] === 'string' ? source['pt'] : '',
    es: typeof source['es'] === 'string' ? source['es'] : '',
  };
};

const readNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const readType = (value: unknown): SurveyQuestionType =>
  typeof value === 'string' && (SURVEY_QUESTION_TYPES as readonly string[]).includes(value)
    ? (value as SurveyQuestionType)
    : 'SHORT_TEXT';

const readRowType = (value: unknown): MatrixRowType =>
  typeof value === 'string' && (MATRIX_ROW_TYPES as readonly string[]).includes(value)
    ? (value as MatrixRowType)
    : 'SINGLE_CHOICE';

const readConfig = (
  source: unknown,
  type: SurveyQuestionType,
): { numbers: QuestionConfigNumbers; texts: Record<SurveyLanguage, QuestionConfigTexts> } => {
  const fields = configFieldsFor(type);
  const document = isRecord(source) ? source : {};
  const numbers: QuestionConfigNumbers = { ...defaultNumbersFor(type) };

  fields.numbers.forEach(field => {
    const value = readNumber(document[field]);
    if (value !== undefined) numbers[field] = value;
  });

  const texts: Record<SurveyLanguage, QuestionConfigTexts> = {
    en: { ...EMPTY_CONFIG_TEXTS },
    pt: { ...EMPTY_CONFIG_TEXTS },
    es: { ...EMPTY_CONFIG_TEXTS },
  };

  CONFIG_TEXT_FIELDS.forEach(field => {
    const entry = readText(document[field]);

    SURVEY_LANGUAGES.forEach(language => {
      texts[language][field] = entry[language];
    });
  });

  return { numbers, texts };
};

const readOption = (source: unknown): SurveyOptionDraft => {
  const document = isRecord(source) ? source : {};

  return {
    uid: nextUid('opt'),
    value: typeof document['value'] === 'string' ? document['value'] : '',
    labels: readText(document['label'] ?? document['labels']),
    isExclusive: document['isExclusive'] === true,
    allowsFreeText: document['allowsFreeText'] === true,
  };
};

const readRow = (source: unknown): SurveyRowDraft => {
  const document = isRecord(source) ? source : {};

  return {
    uid: nextUid('row'),
    key: typeof document['key'] === 'string' ? document['key'] : '',
    keyTouched: true,
    type: readRowType(document['type']),
    labels: readText(document['label'] ?? document['labels']),
    helpTexts: readText(document['helpText']),
    isRequired: document['isRequired'] !== false,
  };
};

const readQuestion = (source: unknown, index: number): SurveyQuestionDraft => {
  const document = isRecord(source) ? source : {};
  const type = readType(document['type']);
  const config = readConfig(document['config'], type);
  const step = readNumber(document['step']);

  return {
    uid: nextUid('q'),
    key: typeof document['key'] === 'string' ? document['key'] : '',
    // A key that arrived in a document is a decision someone wrote down; it must not start
    // following the label again.
    keyTouched: true,
    type,
    step: step !== undefined && step >= 1 ? Math.trunc(step) : index + 1,
    isRequired: document['isRequired'] !== false,
    labels: readText(document['label'] ?? document['labels']),
    helpTexts: readText(document['helpText']),
    numbers: config.numbers,
    configTexts: config.texts,
    options: Array.isArray(document['options'])
      ? (document['options'] as unknown[]).map(option => readOption(option))
      : [],
    rows:
      type === 'MATRIX' && Array.isArray(document['rows'])
        ? (document['rows'] as unknown[]).map(row => readRow(row))
        : [],
  };
};

export const parseQuestionnaire = (raw: string): ParseQuestionnaireResult => {
  let parsed: unknown;

  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    return { ok: false, message: error instanceof Error ? error.message : String(error) };
  }

  if (!isRecord(parsed)) return { ok: false, message: 'O JSON precisa ser um objeto na raiz.' };

  const title = readText(parsed['title']);
  const description = readText(parsed['description']);
  const texts: Record<SurveyLanguage, SurveyTextsDraft> = {
    en: { title: title.en, description: description.en },
    pt: { title: title.pt, description: description.pt },
    es: { title: title.es, description: description.es },
  };

  const questions = Array.isArray(parsed['questions'])
    ? (parsed['questions'] as unknown[]).map((question, index) => readQuestion(question, index))
    : [];

  return { ok: true, content: { texts, questions } };
};

/* ───────────────────────────── Pendências ─────────────────────────────── */

const questionName = (question: SurveyQuestionDraft, index: number): string =>
  question.key.trim() !== '' ? question.key.trim() : `Pergunta ${index + 1}`;

/**
 * What is still missing, listed rather than hidden behind a disabled button.
 *
 * Per language, because that is how a translation goes missing: the questionnaire looks finished
 * in the language the person was typing in, and the gap only shows up for whoever opens the
 * dashboard in another one. The list sums the three languages, and the save button is off while
 * it has entries (R6b) — the `approve` gate is the second net, not the first (R6).
 */
/**
 * A pendência, with enough on it to be acted on.
 *
 * A flat list of sentences was fine while every step was on screen. Once a step can be collapsed,
 * "Pergunta 3: preencha o enunciado em Espanhol" points at something the reader cannot see — so
 * each entry carries where it lives (`step`, `questionUid`), which field to put the cursor in and
 * which language tab has to be open for that field to exist.
 */
export interface QuestionnaireProblem {
  /** Stable within one render, for React keys and for the click target. */
  id: string;
  message: string;
  /** The step it belongs to. `null` for the survey's own texts, which live above the steps. */
  step: number | null;
  questionUid?: string;
  /** The tab that has to be open for the field to be on screen. */
  language?: SurveyLanguage;
  /** `id` of the input to focus. */
  fieldId?: string;
}

const questionFieldId = (question: SurveyQuestionDraft, suffix: string): string =>
  `survey-builder-question-${question.uid}-${suffix}`;

/**
 * What is still missing, listed rather than hidden behind a disabled button.
 *
 * Per language, because that is how a translation goes missing: the questionnaire looks finished
 * in the language the person was typing in, and the gap only shows up for whoever opens the
 * dashboard in another one. The list sums the three languages, and the save button is off while
 * it has entries (R6b) — the `approve` gate is the second net, not the first.
 */
export const getQuestionnaireProblems = (content: QuestionnaireContent): QuestionnaireProblem[] => {
  const problems: QuestionnaireProblem[] = [];

  const add = (entry: QuestionnaireProblem): void => {
    problems.push(entry);
  };

  SURVEY_LANGUAGES.forEach(language => {
    if (content.texts[language].title.trim() === '') {
      add({
        id: `title-${language}`,
        message: `Preencha o título da pesquisa em ${LANGUAGE_NAME[language]}.`,
        step: null,
        language,
        fieldId: 'survey-builder-title',
      });
    }
  });

  if (content.questions.length === 0) {
    add({
      id: 'no-questions',
      message: 'A pesquisa não tem nenhuma pergunta.',
      step: null,
    });
  }

  content.questions.forEach((question, index) => {
    const name = questionName(question, index);
    const at = { step: question.step, questionUid: question.uid };

    if (question.key.trim() === '') {
      add({
        ...at,
        id: `${question.uid}-key`,
        message: `${name}: dê um identificador à pergunta.`,
        language: BASE_LANGUAGE,
        fieldId: questionFieldId(question, 'key'),
      });
    }

    SURVEY_LANGUAGES.forEach(language => {
      if (question.labels[language].trim() === '') {
        add({
          ...at,
          id: `${question.uid}-label-${language}`,
          message: `${name}: preencha o enunciado em ${LANGUAGE_NAME[language]}.`,
          language,
          fieldId: questionFieldId(question, 'label'),
        });
      }
    });

    const carriesOptions =
      question.type === 'SINGLE_CHOICE' ||
      question.type === 'MULTI_CHOICE' ||
      question.type === 'MATRIX';

    if (carriesOptions) {
      const usable = question.options.filter(option => option.value.trim() !== '');

      if (usable.length === 0) {
        add({
          ...at,
          id: `${question.uid}-no-options`,
          message:
            question.type === 'MATRIX'
              ? `${name}: a grade precisa de uma escala — acrescente as opções.`
              : `${name}: acrescente pelo menos uma opção.`,
        });
      }

      const values = usable.map(option => option.value.trim());

      if (new Set(values).size !== values.length) {
        add({
          ...at,
          id: `${question.uid}-duplicate-options`,
          message: `${name}: há duas opções com o mesmo identificador.`,
        });
      }

      question.options.forEach((option, optionIndex) => {
        const optionName =
          option.value.trim() !== '' ? option.value.trim() : `opção ${optionIndex + 1}`;

        if (option.value.trim() === '') {
          add({
            ...at,
            id: `${question.uid}-option-${optionIndex}-value`,
            message: `${name}: dê um identificador à ${optionName}.`,
            language: BASE_LANGUAGE,
            fieldId: questionFieldId(question, `option-value-${optionIndex}`),
          });
        }

        SURVEY_LANGUAGES.forEach(language => {
          if (option.labels[language].trim() === '') {
            add({
              ...at,
              id: `${question.uid}-option-${optionIndex}-label-${language}`,
              message: `${name} · ${optionName}: preencha o rótulo em ${LANGUAGE_NAME[language]}.`,
              language,
              fieldId: questionFieldId(question, `option-label-${optionIndex}`),
            });
          }
        });
      });
    }

    if (question.type === 'MATRIX') {
      if (question.rows.length === 0) {
        add({
          ...at,
          id: `${question.uid}-no-rows`,
          message: `${name}: a grade precisa de pelo menos uma linha.`,
        });
      }

      question.rows.forEach((row, rowIndex) => {
        const rowName = row.key.trim() !== '' ? row.key.trim() : `linha ${rowIndex + 1}`;

        if (row.key.trim() === '') {
          add({
            ...at,
            id: `${question.uid}-row-${rowIndex}-key`,
            message: `${name}: dê um identificador à ${rowName}.`,
            language: BASE_LANGUAGE,
            fieldId: questionFieldId(question, `row-key-${rowIndex}`),
          });
        }

        SURVEY_LANGUAGES.forEach(language => {
          if (row.labels[language].trim() === '') {
            add({
              ...at,
              id: `${question.uid}-row-${rowIndex}-label-${language}`,
              message: `${name} · ${rowName}: preencha o rótulo em ${LANGUAGE_NAME[language]}.`,
              language,
              fieldId: questionFieldId(question, `row-label-${rowIndex}`),
            });
          }
        });
      });
    }

    if (question.type === 'MULTI_CHOICE') {
      const { minSelections, maxSelections } = question.numbers;

      if (
        minSelections !== undefined &&
        maxSelections !== undefined &&
        minSelections > maxSelections
      ) {
        add({
          ...at,
          id: `${question.uid}-selection-range`,
          message: `${name}: o mínimo de seleções é maior que o máximo.`,
          language: BASE_LANGUAGE,
          fieldId: questionFieldId(question, 'config-minSelections'),
        });
      }
    }

    if (question.type === 'RATING') {
      const { min, max } = question.numbers;

      if (min !== undefined && max !== undefined && min >= max) {
        add({
          ...at,
          id: `${question.uid}-scale-range`,
          message: `${name}: o fim da escala precisa ser maior que o começo.`,
          language: BASE_LANGUAGE,
          fieldId: questionFieldId(question, 'config-max'),
        });
      }
    }
  });

  const keys = content.questions.map(question => question.key.trim()).filter(Boolean);

  if (new Set(keys).size !== keys.length) {
    add({
      id: 'duplicate-keys',
      message: 'Há duas perguntas com o mesmo identificador.',
      step: null,
    });
  }

  if (content.questions.length > SURVEY_LIMITS.questions) {
    add({
      id: 'too-many-questions',
      message: `A pesquisa passa de ${SURVEY_LIMITS.questions} perguntas.`,
      step: null,
    });
  }

  const steps = new Set(content.questions.map(question => question.step));

  if (steps.size > SURVEY_LIMITS.steps) {
    add({
      id: 'too-many-steps',
      message: `A pesquisa passa de ${SURVEY_LIMITS.steps} passos.`,
      step: null,
    });
  }

  return problems;
};

/** The content half of a form, for the JSON box and the export button. */
export const contentFromForm = (form: SurveyFormData): QuestionnaireContent => ({
  texts: form.texts,
  questions: form.questions,
});
